import React from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { AuthLayout } from './layouts/AuthLayout';
import { MainLayout } from './layouts/MainLayout';
import { AddEventPage } from './pages/AddEventPage';
import { EditEventPage } from './pages/EditEventPage';
import { EventDetailsPage } from './pages/EventDetailsPage';
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { ProfilePage } from './pages/ProfilePage';
import { UserProfilePage } from './pages/UserProfilePage';
import { ModerationDashboard } from './pages/admin/ModerationDashboard';
import { CreateBlogPage } from './pages/admin/CreateBlogPage';
import { EditBlogPage } from './pages/admin/EditBlogPage';
import { BlogListPage } from './pages/admin/BlogListPage';
import { BlogViewPage } from './pages/BlogViewPage';
import { BlogsPage } from './pages/BlogsPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { FeedPage } from './pages/FeedPage';

// Protected Route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route component (redirects to home if already authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

// Admin Route component (requires authentication AND admin status)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <PublicRoute>
        <AuthLayout>
          <LoginPage />
        </AuthLayout>
      </PublicRoute>
    ),
  },
  {
    path: "/",
    element: (
      <MainLayout>
        <HomePage />
      </MainLayout>
    ),
  },
  {
    path: "/add-event",
    element: (
      <ProtectedRoute>
        <MainLayout>
          <AddEventPage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/events/:id/edit",
    element: (
      <ProtectedRoute>
        <MainLayout>
          <EditEventPage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/events/:id",
    element: (
        <MainLayout>
          <EventDetailsPage />
        </MainLayout>
    ),
  },
  {
    path: "/profile",
    element: (
      <ProtectedRoute>
        <MainLayout>
          <ProfilePage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/:id",
    element: (
      <ProtectedRoute>
        <MainLayout>
          <UserProfilePage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/admin/moderation",
    element: (
      <AdminRoute>
        <MainLayout>
          <ModerationDashboard />
        </MainLayout>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/blog",
    element: (
      <AdminRoute>
        <MainLayout>
          <BlogListPage />
        </MainLayout>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/blog/create",
    element: (
      <AdminRoute>
        <MainLayout>
          <CreateBlogPage />
        </MainLayout>
      </AdminRoute>
    ),
  },
  {
    path: "/admin/blog/edit/:id",
    element: (
      <AdminRoute>
        <MainLayout>
          <EditBlogPage />
        </MainLayout>
      </AdminRoute>
    ),
  },
  {
    path: "/feed",
    element: (
      <ProtectedRoute>
        <MainLayout>
          <FeedPage />
        </MainLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: "/blogs",
    element: (
      <MainLayout>
        <BlogsPage />
      </MainLayout>
    ),
  },
  {
    path: "/blog/:id",
    element: (
      <MainLayout>
        <BlogViewPage />
      </MainLayout>
    ),
  },
  {
    path: "/terms",
    element: (
      <MainLayout>
        <TermsPage />
      </MainLayout>
    ),
  },
  {
    path: "/privacy",
    element: (
      <MainLayout>
        <PrivacyPage />
      </MainLayout>
    ),
  },
  {
    path: "*",
    element: (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">404</h1>
          <p className="text-gray-600 mb-8">Page not found</p>
          <a href="/" className="btn-primary">
            Go Home
          </a>
        </div>
      </div>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}